import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { registerLicense } from '@syncfusion/ej2-base';
import NavigationBar from './components/NavigationBar';
import Loader from './components/loader/Loader';
import { useActions } from './redux/Actions';
import RoutesPage from './routes/RoutesPage';
import './styles/App.scss';
import { constantVar } from './utils/constant';
import useConfig from './utils/useConfig';
import { useEffect } from 'react';

// Registering Syncfusion license key
registerLicense(constantVar?.syncfusionKey);

// Define interface for config object
interface OidcConfig {
  url: string;
  clientId: string;
}

const AuthContent = () => {
  const auth = useAuth();
  const { setUserData, setToken } = useActions();

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      handleAuthSuccess();
    }
  }, [auth.isAuthenticated, auth.user]);

  /**
   * Handles successful authentication
   * - Stores authentication state
   * - Sets user data in the store
   * - Stores user information in local storage
   */
  const handleAuthSuccess = () => {
    if (auth.user && auth.user.access_token) {
      localStorage.setItem('isAuthenticated', 'true');

      // Store token in state
      setToken(auth.user.access_token);

      // Create user payload
      const userPayload = {
        homeAccountId: auth.user.profile.sub || '',
        environment: 'OIDC',
        tenantId: auth.user.profile.iss || '',
        username: auth.user.profile.preferred_username || '',
        localAccountId: auth.user.profile.sub || '',
        name: auth.user.profile.name || '',
        authorityType: 'OIDC',
        clientId: auth.settings.client_id || '',
        token: auth.user.access_token || '',
      };
      

      try {
        // Set user data in the store
        setUserData({
          AuthorityType: userPayload.authorityType,
          ClientId: userPayload.clientId,
          Environment: userPayload.environment,
          HomeAccountId: userPayload.homeAccountId,
          LocalAccountId: userPayload.localAccountId,
          Name: userPayload.name,
          TenantId: userPayload.tenantId,
          Username: userPayload.username,
        });

        // Store user information in local storage
        const displayName =
          userPayload.username === '' ? userPayload.name : userPayload.username;
        localStorage.setItem('loggedInUser', displayName);
        localStorage.setItem('viewMavenUser', displayName);
      } catch (error) {
        console.error('Error setting user data:', error);
      }
    }
  };

  if (auth.isLoading) {
    return <Loader />;
  }

  if (auth.error) {
    return <div>Oops... {auth.error.message}</div>;
  }

  return (
    <>
      <Toaster />
      <NavigationBar />
      <RoutesPage />
    </>
  );
};

const App: React.FC = () => {
  const [configData, _, isLoading] = useConfig();

  // Show loader while config is loading
  if (isLoading || !configData) {
    return <Loader />;
  }

  // Cast configData to proper type with type safety check
  const config = configData as unknown as OidcConfig;
  // Configuration for OIDC provider
  const oidcConfig = {
    authority: config.url,
    client_id: config.clientId,
    redirect_uri: window.location.origin,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    // scope: 'openid profile email',
    automaticSilentRenew: true,
    // loadUserInfo: true,
    onSigninCallback: () => {
      // Remove the query parameters from the URL after successful login
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  };

  return (
    <AuthProvider {...oidcConfig}>
      <div className="App" id="target">
        <AuthContent />
      </div>
    </AuthProvider>
  );
};

export default App;
